import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Button, Card, Badge, Logo } from "../components/kit";
import api, { mediaUrl } from "../lib/api";
import { LogoUploader } from "../components/LogoUploader";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PlanPanel, CohortsPanel, TeamPanel, goToPlan } from "../components/CoachPlan";

const DELIVERY = [["one_on_one", "One-on-one"], ["small_group", "Small group"], ["events", "Large-scale events"], ["async", "Async content"], ["hybrid", "Hybrid"]];
const WISH = [["action", "Who's taking action"], ["risk", "Who's at risk"], ["blocker", "What's blocking the group"]];

export default function CoachSettings() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const location = useLocation();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (user?.team_member) { navigate("/coach"); return; }
    api.get("/coach/profile").then((r) => {
      if (!r.data.onboarded) { navigate("/coach/onboarding"); return; }
      setP({ ...r.data, member_count: r.data.member_count ?? 0, accent_color: r.data.branding.accent_color, logo_url: r.data.branding.logo_url });
    }).catch(() => navigate("/coach"));
  }, []); // eslint-disable-line
  useEffect(() => { if (p && location.hash === "#plan") setTimeout(goToPlan, 300); }, [!!p]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/coach/profile", {
        program_name: p.program_name, member_count: parseInt(p.member_count || "0", 10),
        delivery_mode: p.delivery_mode, hybrid_lean: p.delivery_mode === "hybrid" ? p.hybrid_lean : null,
        wish_knew: p.wish_knew,
        logo_url: p.logo_url, accent_color: p.accent_color,
      });
      await refreshUser();
      toast.success("Settings saved", { description: "Applied the next time your dashboard loads." });
      navigate("/coach");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  if (!p) return <div className="buktu-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}><p style={{ color: "var(--text-muted)" }}>Loading…</p></div>;

  return (
    <div className="buktu-root" data-brand="catalyst" data-testid="coach-settings" style={{ minHeight: "100vh", background: "var(--surface-page)", "--catalyst-accent": p.accent_color }}>
      <div className="buktu-wrap" style={{ maxWidth: 680, paddingTop: "var(--space-8)", paddingBottom: "var(--space-20)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
          <Logo height={28} to="catalyst" />
          <Badge tone="accent" dot>Coach settings</Badge>
          <div style={{ marginLeft: "auto" }}>
            <Button variant="ghost" size="sm" data-testid="settings-back" onClick={() => navigate("/coach")} iconLeft={<ArrowLeft size={16} />}>Back to dashboard</Button>
          </div>
        </div>

        <PlanPanel />
        <CohortsPanel isOwner />
        <TeamPanel isOwner />

        <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-5)" }}>
          <h3 style={{ font: "var(--type-heading)", marginBottom: "var(--space-4)" }}>Program</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <L label="Program name"><input data-testid="set-program" className="buktu-input" value={p.program_name} onChange={(e) => set("program_name", e.target.value)} /></L>
            <L label="Active member count"><input data-testid="set-members" className="buktu-input" type="number" min="0" value={p.member_count} onChange={(e) => set("member_count", e.target.value)} /></L>
            <L label="Delivery mode">
              <select data-testid="set-delivery" className="buktu-input" value={p.delivery_mode} onChange={(e) => set("delivery_mode", e.target.value)}>
                {DELIVERY.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </L>
            {p.delivery_mode === "hybrid" && (
              <L label="Hybrid — where does your week mostly go?">
                <select data-testid="set-hybrid" className="buktu-input" value={p.hybrid_lean || ""} onChange={(e) => set("hybrid_lean", e.target.value)}>
                  <option value="">Choose…</option>
                  <option value="individual">More with individuals</option>
                  <option value="group">More with the group</option>
                </select>
              </L>
            )}
            <L label="Pinned insight (what you most want to know)">
              <select data-testid="set-wish" className="buktu-input" value={p.wish_knew} onChange={(e) => set("wish_knew", e.target.value)}>
                {WISH.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </L>
          </div>
        </Card>

        <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-5)" }}>
          <h3 style={{ font: "var(--type-heading)", marginBottom: "var(--space-4)" }}>Branding</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <L label="Brand accent color">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input data-testid="set-accent" type="color" value={p.accent_color} onChange={(e) => set("accent_color", e.target.value)} style={{ width: 52, height: 40, border: "1px solid var(--border-hairline)", borderRadius: 8, cursor: "pointer", background: "none" }} />
                <span style={{ font: "var(--type-data)", color: "var(--text-muted)" }}>{p.accent_color}</span>
              </div>
            </L>
            <L label="Logo">
              <LogoUploader value={p.logo_url} accent={p.accent_color} onUploaded={(u) => set("logo_url", u)} />
            </L>
          </div>
        </Card>

        <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-5)" }} data-testid="brand-preview">
          <h3 style={{ font: "var(--type-heading)", marginBottom: "var(--space-4)" }}>Live preview</h3>
          <div style={{ borderRadius: "var(--radius-lg)", border: "1px solid var(--border-hairline)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", background: "#0b3a30" }}>
              {p.logo_url ? <img src={mediaUrl(p.logo_url)} alt="logo" data-testid="brand-preview-logo" style={{ height: 28, maxWidth: 160, objectFit: "contain" }} /> : <span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>buktu</span>}
            </div>
            <div style={{ padding: 18, background: "var(--surface-page)" }}>
              <span className="buktu-eyebrow" style={{ color: p.accent_color }}>Catalyst · coach cohort</span>
              <h4 style={{ font: "var(--type-title)", fontSize: 22 }}>{p.program_name || "Your program"}</h4>
              <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ padding: "8px 16px", borderRadius: 999, background: p.accent_color, color: "#fff", font: "var(--type-label)", fontSize: 13 }}>Accent button</span>
                <span style={{ padding: "8px 16px", borderRadius: 999, border: `1px solid ${p.accent_color}`, color: p.accent_color, font: "var(--type-label)", fontSize: 13 }}>Outline</span>
              </div>
            </div>
          </div>
          <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: 10 }}>This is how your logo and accent appear on your dashboard. Save to apply.</p>
        </Card>

        <Card padding="var(--space-6)" style={{ marginBottom: "var(--space-6)" }} data-testid="settings-trust">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "var(--space-3)" }}>
            <span style={{ color: "var(--buktu-green)" }}><ShieldCheck size={18} /></span>
            <h3 style={{ font: "var(--type-heading)" }}>Member privacy (read-only)</h3>
          </div>
          <p style={{ font: "var(--type-body-sm)", color: "var(--text-body)", lineHeight: 1.6 }}>
            Your members control what you can see. You see aggregate, anonymized patterns by default; an individual's detail appears only if that member has explicitly turned sharing on. Raw financial or sentiment data is never shown without their opt-in.
          </p>
          {p.trust_ack_at && <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: 8 }}>Acknowledged {new Date(p.trust_ack_at).toLocaleDateString()}</p>}
        </Card>

        <Button data-testid="settings-save" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}
function L({ label, children }) {
  return <label style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", font: "var(--type-label)", color: "var(--text-strong)" }}>{label}{children}</label>;
}
